import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '@/firebaseInit';
import {
  coll_settings,
  doc_invoicingSettings,
  doc_siteSettings,
  doc_termsSettings,
  normaliseInvoicingSettings,
  normaliseSiteSettings,
  normaliseTermsSettings,
  type InvoicingSettingsType,
  type SiteSettingsType,
  type TermsSettingsType,
} from '@/types/types_settings';

// Data-access layer for the settings documents.

const siteSettingsRef = doc(db, coll_settings, doc_siteSettings);
const invoicingSettingsRef = doc(db, coll_settings, doc_invoicingSettings);
const termsSettingsRef = doc(db, coll_settings, doc_termsSettings);

/**
 * Subscribes to the site settings. Returns an unsubscribe function.
 *
 * A missing document is not an error — it is the state before anybody has
 * touched a setting — so it yields the defaults, and the site behaves the same
 * whether the collection is empty or was deliberately set that way.
 */
export function subscribeSiteSettings(
  onChange: (settings: SiteSettingsType) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    siteSettingsRef,
    (snap) => onChange(normaliseSiteSettings(snap.data() as Partial<SiteSettingsType> | undefined)),
    onError,
  );
}

/**
 * Writes some of the settings, leaving the rest alone.
 *
 * A merge rather than a whole-document write: two admins on the settings screen
 * changing different switches should not silently undo each other, and a client
 * that has not yet loaded a flag added since its bundle was built must not
 * write it back as a default.
 */
export async function updateSiteSettings(
  changes: Partial<SiteSettingsType>,
  uid: string | undefined,
): Promise<void> {
  await setDoc(
    siteSettingsRef,
    { ...changes, updatedAt: new Date().toISOString(), updatedByUid: uid ?? null },
    { merge: true },
  );
}

/**
 * Subscribes to the invoicing settings — what the monthly invoice prints about
 * us. Admins only; the rules refuse this document to anyone else.
 *
 * A missing document yields the defaults, as above. Unlike the site settings
 * those defaults are not sufficient to invoice with: see
 * `missingInvoicingSettings`, which is what the issue path checks.
 */
export function subscribeInvoicingSettings(
  onChange: (settings: InvoicingSettingsType) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    invoicingSettingsRef,
    (snap) => onChange(normaliseInvoicingSettings(snap.data() as Partial<InvoicingSettingsType> | undefined)),
    onError,
  );
}

/** Writes some of the invoicing settings, merging for the same reason as above. */
export async function updateInvoicingSettings(
  changes: Partial<InvoicingSettingsType>,
  uid: string | undefined,
): Promise<void> {
  await setDoc(
    invoicingSettingsRef,
    { ...changes, updatedAt: new Date().toISOString(), updatedByUid: uid ?? null },
    { merge: true },
  );
}

/**
 * Subscribes to the partner agreement.
 *
 * Readable without signing in, unlike either of the above: the registration
 * wizard shows the agreement at step 2 and creates the account at step 3, so the
 * person it binds reads it as a stranger. A missing document yields blank terms
 * — there is no wording compiled in behind this one — and the step says so.
 */
export function subscribeTermsSettings(
  onChange: (settings: TermsSettingsType) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    termsSettingsRef,
    (snap) => onChange(normaliseTermsSettings(snap.data() as Partial<TermsSettingsType> | undefined)),
    onError,
  );
}

/** Writes some of the terms, merging for the same reason as above. */
export async function updateTermsSettings(
  changes: Partial<TermsSettingsType>,
  uid: string | undefined,
): Promise<void> {
  await setDoc(
    termsSettingsRef,
    { ...changes, updatedAt: new Date().toISOString(), updatedByUid: uid ?? null },
    { merge: true },
  );
}
